import React from 'react';
import { captureException } from 'utils/error-tracker';
import styles from './ErrorBoundary.module.scss';

export interface CaughtReactError {
  error: Error;
  info: React.ErrorInfo;
}

interface Props {
  FallbackComponent?: React.ComponentType<CaughtReactError> | null;
  onError?: (props: CaughtReactError) => void;
  catchInDev?: boolean;
  children?: React.ReactNode;
}

interface State {
  caughtError: CaughtReactError | null;
}

class ErrorBoundary extends React.Component<Props, State> {
  state: State = { caughtError: null };

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    captureException(error, {
      extra: { key: 'componentStack', value: info.componentStack || '' },
      level: 'fatal',
    });
    this.props.onError?.({ error, info });
    this.setState({ caughtError: { error, info } });
  }

  render() {
    const { children, FallbackComponent = DefaultFallbackComponent } = this.props;

    // We don't want ErrorBoundaries by default in development, makes troubleshooting more difficult
    if (__DEVELOPMENT__ && !this.props.catchInDev) {
      return <>{children}</>;
    }

    const { caughtError } = this.state;
    if (caughtError !== null) {
      return FallbackComponent ? <FallbackComponent {...caughtError} /> : null;
    }

    return children || null;
  }
}

export default ErrorBoundary;

const toTitle = (error: Error, componentStack?: string | null): string => {
  return `${error.toString()}\n\nThis is located at:${componentStack}`;
};

const DefaultFallbackComponent = ({ info, error }: CaughtReactError) => (
  <div className={styles.defaultFallbackComponent} title={toTitle(error, info.componentStack)}>
    <svg viewBox="0 0 24 24" preserveAspectRatio="xMidYMid">
      <path
        d={`M20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,
        12M22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2A10,10 0 0,1 22,
        12M15.5,8C16.3,8 17,8.7 17,9.5C17,10.3 16.3,11 15.5,11C14.7,11 14,10.3 14,
        9.5C14,8.7 14.7,8 15.5,8M10,9.5C10,10.3 9.3,11 8.5,11C7.7,11 7,10.3 7,9.5C7,
        8.7 7.7,8 8.5,8C9.3,8 10,8.7 10,9.5M12,14C13.75,14 15.29,14.72 16.19,
        15.81L14.77,17.23C14.32,16.5 13.25,16 12,16C10.75,16 9.68,16.5 9.23,
        17.23L7.81,15.81C8.71,14.72 10.25,14 12,14Z`}
      />
    </svg>
  </div>
);
